import React, { useRef, useState } from 'react';
import { api } from '../api';
import { Toast, css, PageHeader, Spinner } from '../components/UI';
import { SelectorCuentaPago } from '../components/CuentaPago';

// Carga masiva de comprobantes de pago (backend: routes/comprobantesMasivos.js).
// 1. Tesorería suelta los PDF del banco.  2. El sistema lee cada uno y propone su
// solicitud.  3. La persona revisa y confirma: solo entonces se adjuntan y las
// solicitudes quedan pagadas, con la fecha y la cuenta de cada comprobante.
const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fechaCorta = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const FORMATO = { bbva_interbancario: 'BBVA · interbancario', bbva_cie: 'BBVA · pago CIE', bbva_cuenta_bbva: 'BBVA · mismo banco', tesoreria: 'Tesorería CDMX', sat: 'SAT · contribuciones federales', imss: 'IMSS-INFONAVIT (SUA)', desconocido: 'Formato no reconocido' };
const ESTADO = {
  segura:     { texto: 'Segura', color: 'var(--ok)', ayuda: 'Coincide el importe y el beneficiario, la CLABE o el folio' },
  revisar:    { texto: 'Revisar', color: 'var(--st-pendiente)', ayuda: 'Es la única con ese importe, pero nada más coincide: confírmala' },
  elegir:     { texto: 'Elegir', color: 'var(--st-pendiente)', ayuda: 'Hay varias solicitudes posibles: elige cuál' },
  sin_pareja: { texto: 'Sin pareja', color: 'var(--glass)', ayuda: 'No hay solicitud por pagar con ese importe' },
  duplicado:  { texto: 'Ya usado', color: 'var(--danger)', ayuda: 'Este comprobante ya se usó' },
  ilegible:   { texto: 'No se pudo leer', color: 'var(--danger)', ayuda: 'El PDF no trae texto' },
};

export default function ComprobantesMasivos() {
  const inputRef = useRef(null);
  const [archivos, setArchivos] = useState([]);
  const [filas, setFilas] = useState(null);   // resultado del análisis + elección de la persona
  const [leyendo, setLeyendo] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 5000); };

  const leer = async (lista) => {
    const pdfs = [...lista].filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) { toast_('Elige comprobantes en PDF', 'error'); return; }
    if (pdfs.length > 50) { toast_('Son hasta 50 comprobantes por lote', 'error'); return; }
    setArchivos(pdfs); setFilas(null); setResultado(null); setLeyendo(true);
    try {
      const fd = new FormData();
      pdfs.forEach(f => fd.append('archivos', f));
      const r = await api.upload('/comprobantes-masivos/analizar', fd);
      setFilas(r.resultados.map(x => ({
        ...x,
        elegida: x.propuesta ? `${x.propuesta.coleccion}:${x.propuesta.id}` : '',
        incluir: ['segura', 'revisar'].includes(x.estado),
        cuentaPago: x.cuentaPago?.id || '',
        cuentaPagoDetectada: x.cuentaPago?.id || '',
      })));
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLeyendo(false); }
  };

  const cambiar = (i, cambios) => setFilas(fs => fs.map((f, k) => (k === i ? { ...f, ...cambios } : f)));
  const opcionDe = (f) => (f.opciones || []).find(o => `${o.coleccion}:${o.id}` === f.elegida);
  const listas = (filas || []).filter(f => f.incluir && f.elegida);
  const repetidas = (() => { const n = {}; listas.forEach(f => { n[f.elegida] = (n[f.elegida] || 0) + 1; }); return new Set(Object.keys(n).filter(k => n[k] > 1)); })();
  const conteo = (filas || []).reduce((c, f) => ({ ...c, [f.estado]: (c[f.estado] || 0) + 1 }), {});

  const confirmar = async () => {
    if (!listas.length) return;
    if (repetidas.size) { toast_('Una misma solicitud está elegida para dos comprobantes', 'error'); return; }
    if (!window.confirm(`Se adjuntarán ${listas.length} comprobantes y esas solicitudes quedarán PAGADAS. Se avisará a cada solicitante por correo. ¿Continuar?`)) return;
    setConfirmando(true);
    try {
      const fd = new FormData();
      archivos.forEach(f => fd.append('archivos', f));
      fd.append('asignaciones', JSON.stringify(listas.map(f => { const o = opcionDe(f); return { indice: f.indice, coleccion: o.coleccion, id: o.id, cuentaPago: f.cuentaPago || undefined }; })));
      const r = await api.upload('/comprobantes-masivos/confirmar', fd);
      setResultado(r.resultados);
      toast_(`${r.pagadas} ${r.pagadas === 1 ? 'solicitud quedó pagada' : 'solicitudes quedaron pagadas'}`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setConfirmando(false); }
  };

  const reiniciar = () => { setArchivos([]); setFilas(null); setResultado(null); if (inputRef.current) inputRef.current.value = ''; };
  const th = { padding: '9px 10px', textAlign: 'left', fontWeight: 600, verticalAlign: 'bottom' };
  const td = { padding: '10px', borderTop: '1px solid var(--mesh-soft)', verticalAlign: 'top' };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Subir comprobantes de pago"
        subtitle="Suelta los comprobantes del banco: el sistema propone a qué solicitud va cada uno. Nada se marca como pagado hasta que confirmes." />

      {!filas && !leyendo && (
        <div
          onDragOver={e => { e.preventDefault(); setArrastrando(true); }} onDragLeave={() => setArrastrando(false)}
          onDrop={e => { e.preventDefault(); setArrastrando(false); leer(e.dataTransfer.files); }}
          style={{ ...css.card, padding: 36, textAlign: 'center', border: `2px dashed ${arrastrando ? 'var(--plasma)' : 'var(--hair)'}`, background: arrastrando ? 'var(--panel-hi)' : undefined }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>Arrastra aquí los comprobantes en PDF</div>
          <div style={{ fontSize: 13, color: 'var(--glass)', marginBottom: 16 }}>Hasta 50 por lote, de 40 MB máximo cada uno. Lee BBVA Net Cash (interbancario, CIE, mismo banco), recibos del SAT, del IMSS-INFONAVIT y de la Tesorería de la CDMX.</div>
          <input ref={inputRef} type="file" multiple accept=".pdf,application/pdf" onChange={e => leer(e.target.files)} style={{ display: 'none' }} />
          <button type="button" onClick={() => inputRef.current?.click()} style={css.btn('#ff541c')}>Elegir archivos</button>
        </div>
      )}

      {leyendo && <div style={{ ...css.card, padding: 24, textAlign: 'center' }}><Spinner /><div style={{ fontSize: 13, color: 'var(--glass)', marginTop: 8 }}>Leyendo {archivos.length} comprobantes…</div></div>}

      {filas && !resultado && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 12px', fontSize: 13 }}>
            {Object.entries(ESTADO).filter(([k]) => conteo[k]).map(([k, e]) => (
              <span key={k} title={e.ayuda}><b style={{ color: e.color }}>{conteo[k]}</b> {e.texto.toLowerCase()}</span>
            ))}
            <span style={{ flex: 1 }} />
            <button type="button" onClick={reiniciar} style={css.btnGhost}>Empezar de nuevo</button>
            <button type="button" onClick={confirmar} disabled={!listas.length || confirmando} style={{ ...css.btn('#ff541c'), opacity: !listas.length || confirmando ? 0.6 : 1 }}>
              {confirmando ? 'Confirmando…' : `Confirmar ${listas.length} ${listas.length === 1 ? 'pago' : 'pagos'}`}
            </button>
          </div>
          <div style={{ ...css.card, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 980 }}>
              <thead><tr>
                <th style={{ ...th, width: 34 }} aria-label="Incluir" />
                <th style={th}>Comprobante</th>
                <th style={th}>Lo que dice</th>
                <th style={{ ...th, width: '34%' }}>Solicitud</th>
                <th style={{ ...th, width: 200 }}>Cuenta de origen</th>
              </tr></thead>
              <tbody>
                {filas.map((f, i) => {
                  const e = ESTADO[f.estado] || ESTADO.sin_pareja;
                  const o = opcionDe(f);
                  const d = f.datos || {};
                  const sinOpciones = !(f.opciones || []).length;
                  return (
                    <tr key={f.indice} style={{ opacity: f.incluir || !sinOpciones ? 1 : 0.7 }}>
                      <td style={td}>
                        <input type="checkbox" checked={f.incluir && !!f.elegida} disabled={!f.elegida} onChange={ev => cambiar(i, { incluir: ev.target.checked })} aria-label={`Incluir ${f.nombre}`} />
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{f.nombre}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{FORMATO[d.formato] || ''}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: e.color, marginTop: 4 }} title={e.ayuda}>{e.texto}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontFamily: 'var(--digit-font)', fontWeight: 600 }}>{fmt(d.importe)}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{fechaCorta(d.fecha)}{d.beneficiario ? ` · ${d.beneficiario}` : d.concepto ? ` · ${d.concepto}` : ''}</div>
                        {d.cuentaRetiro && <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>Retiro ••{d.cuentaRetiro.slice(-4)}{d.cuentaDestino ? ` → ${d.cuentaDestino.length > 12 ? 'CLABE …' + d.cuentaDestino.slice(-4) : d.cuentaDestino}` : ''}</div>}
                        {d.referencia && <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>Ref. {d.referencia}</div>}
                      </td>
                      <td style={td}>
                        {sinOpciones ? <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>{f.motivo || e.ayuda}{f.estado === 'sin_pareja' ? '. Márcala pagada desde su historial.' : ''}</div> : (
                          <>
                            <select value={f.elegida} onChange={ev => cambiar(i, { elegida: ev.target.value, incluir: !!ev.target.value, cuentaPago: f.cuentaPagoDetectada })} style={{ ...css.select(!f.elegida), fontSize: 12.5 }}>
                              <option value="">{f.estado === 'elegir' ? 'Elige la solicitud…' : 'Ninguna (no pagar)'}</option>
                              {f.opciones.map(op => (
                                <option key={`${op.coleccion}:${op.id}`} value={`${op.coleccion}:${op.id}`}>{op.folio} · {op.beneficiario} · {fmt(op.neto)}</option>
                              ))}
                            </select>
                            {o && <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>{o.empresa} · {o.motivo}{o.razones?.length ? ` · ${o.razones.join(', ')}` : ''}</div>}
                            {f.motivo && <div style={{ fontSize: 11.5, color: 'var(--st-pendiente)', marginTop: 2 }}>{f.motivo}</div>}
                            {repetidas.has(f.elegida) && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 2 }}>Esta solicitud está elegida para otro comprobante</div>}
                          </>
                        )}
                      </td>
                      <td style={td}>
                        {o && <SelectorCuentaPago key={f.elegida} empresa={o.empresa} value={f.cuentaPago} onChange={v => cambiar(i, { cuentaPago: v })} />}
                        {o && f.cuentaPago && f.cuentaPago === f.cuentaPagoDetectada && <div style={{ fontSize: 11, color: 'var(--glass)' }}>la del comprobante</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {resultado && (
        <div style={{ ...css.card, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr><th style={th}>Comprobante</th><th style={th}>Resultado</th></tr></thead>
            <tbody>
              {resultado.map(r => (
                <tr key={r.indice}>
                  <td style={td}>{r.nombre}</td>
                  <td style={{ ...td, color: r.ok ? 'var(--ok)' : 'var(--danger)' }}>
                    {r.ok ? `${r.folio} pagada el ${fechaCorta(r.fechaPago)}${r.cuenta ? ` desde ${r.cuenta}` : ''}` : r.error}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: 14, textAlign: 'right' }}><button type="button" onClick={reiniciar} style={css.btn('#ff541c')}>Subir otro lote</button></div>
        </div>
      )}
    </div>
  );
}
